/**
 * apps/web/src/lib/reportError.ts
 *
 * The single client-side error sink. Every error boundary — and the offline
 * write-queue — funnels caught errors through here. It does two things:
 *   1. always records the error in a bounded in-memory ring buffer, queryable via
 *      getRecentReportedErrors() (support / a debug overlay can inspect the last
 *      few failures without a backend);
 *   2. forwards to a registered sink, if any, for real telemetry.
 *
 * No console output (kept out of production per coding-standards). Wiring a
 * backend is a one-line change at boot:
 *   setErrorSink((err, ctx) => Sentry.captureException(err, { extra: ctx }));
 */

export interface ReportedErrorContext {
  /** A stable label for where the error was caught (e.g. "route", "app-root"). */
  readonly boundary?: string;
  /** React component stack, when reported from an error boundary. */
  readonly componentStack?: string;
  readonly [key: string]: unknown;
}

export interface ReportedError {
  readonly at: number;
  readonly message: string;
  readonly name: string;
  readonly context?: ReportedErrorContext;
}

export type ErrorSink = (error: unknown, context?: ReportedErrorContext) => void;

const RING_SIZE = 50;
const ring: ReportedError[] = [];
let sink: ErrorSink | null = null;

/** Register (or clear, with `null`) the process-wide telemetry sink. */
export function setErrorSink(next: ErrorSink | null): void {
  sink = next;
}

/** The most recent reported errors (newest last), for support / debug overlays. */
export function getRecentReportedErrors(): readonly ReportedError[] {
  return ring.slice();
}

function toRecord(error: unknown, context?: ReportedErrorContext): ReportedError {
  const base: ReportedError = {
    at: Date.now(),
    message: error instanceof Error ? error.message : String(error),
    name: error instanceof Error ? error.name : 'Error',
  };
  return context ? { ...base, context } : base;
}

/**
 * Record an error to the ring buffer and forward it to the sink. Never throws —
 * a failing sink must not break the boundary that is already handling an error.
 */
export function reportError(error: unknown, context?: ReportedErrorContext): void {
  ring.push(toRecord(error, context));
  if (ring.length > RING_SIZE) ring.shift();
  if (!sink) return;
  try {
    sink(error, context);
  } catch {
    // A telemetry failure must never escalate the original error.
  }
}
